import { GoogleAuthConfig, AuthState, GoogleUser, AppError, OwnerConfig } from '../types';
import { CONFIG } from '../config';

// Extender la interfaz Window para incluir gapi y Google Identity Services
declare global {
  interface Window {
    gapi: any;
    google: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: () => void;
          renderButton: (element: HTMLElement, config: any) => void;
        };
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: any) => void;
          }) => {
            callback: (response: any) => void;
            requestAccessToken: (options?: { prompt?: string }) => void;
          };
        };
      };
    };
  }
}

/**
 * Gestor de autenticación con Google OAuth
 */
export class AuthManager {
  private authConfig: GoogleAuthConfig;
  private ownerConfig: OwnerConfig;
  private authState: AuthState;
  private gapi: any; // Se cargará dinámicamente

  constructor() {
    this.authConfig = CONFIG.google;
    this.ownerConfig = CONFIG.owner;

    // Intentar restaurar el estado de autenticación desde localStorage
    this.authState = this.loadAuthStateFromStorage();
  }

  /**
   * Carga el estado de autenticación desde localStorage
   */
  private loadAuthStateFromStorage(): AuthState {
    try {
      const savedState = localStorage.getItem('calendarChecker_authState');
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        console.log('🔄 Estado de autenticación restaurado desde localStorage:', parsedState);
        return parsedState;
      }
    } catch (error) {
      console.warn('Error cargando estado de autenticación:', error);
    }
    
    return { isSignedIn: false };
  }

  /**
   * Guarda el estado de autenticación en localStorage
   */
  private saveAuthStateToStorage(): void {
    try {
      localStorage.setItem('calendarChecker_authState', JSON.stringify(this.authState));
      console.log('💾 Estado de autenticación guardado en localStorage');
    } catch (error) {
      console.warn('Error guardando estado de autenticación:', error);
    }
  }

  /**
   * Limpia el estado de autenticación del localStorage
   */
  private clearAuthStateFromStorage(): void {
    try {
      localStorage.removeItem('calendarChecker_authState');
      console.log('🗑️ Estado de autenticación eliminado de localStorage');
    } catch (error) {
      console.warn('Error eliminando estado de autenticación:', error);
    }
  }

  /**
   * Inicializa la API de Google
   */
  private async initializeGoogleAPI(): Promise<void> {
    console.log('🚀 Inicializando Google API con Google Identity Services...');
    
    try {
      // Cargar Google Identity Services y gapi
      await Promise.all([
        this.loadGoogleIdentityServices(),
        this.loadGoogleAPI()
      ]);

      this.gapi = window.gapi;

      // Inicializar gapi.client solamente
      console.log('Inicializando gapi.client...');
      await new Promise<void>((resolve, reject) => {
        this.gapi.load('client', {
          callback: resolve,
          onerror: reject,
          timeout: 5000,
          ontimeout: reject
        });
      });

      // Configurar el cliente
      console.log('Configurando cliente con discovery docs...');
      await this.gapi.client.init({
        discoveryDocs: this.authConfig.discoveryDocs
      });

      // Verificar que la API de Calendar está cargada
      console.log('Verificando disponibilidad de Calendar API...');
      if (!this.gapi.client.calendar) {
        throw new Error('Google Calendar API no se cargó correctamente');
      }
      console.log('✅ Google Calendar API cargada exitosamente');

      // Inicializar Google Identity Services para OAuth
      console.log('Inicializando Google Identity Services...');
      if (window.google && window.google.accounts) {
        // Configurar OAuth con GIS
        window.google.accounts.id.initialize({
          client_id: this.authConfig.clientId,
          callback: (response: any) => {
            console.log('Callback de Google Identity Services:', response);
          }
        });

        console.log('Google Identity Services inicializado correctamente');
      } else {
        throw new Error('Google Identity Services no está disponible');
      }

    } catch (error) {
      console.error('Error inicializando Google API:', error);
      throw this.createAppError('API_LOAD_FAILED', 'Error al cargar la API de Google', error);
    }
  }

  /**
   * Carga dinámicamente Google Identity Services
   */
  private async loadGoogleIdentityServices(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.accounts) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        console.log('Google Identity Services cargado');
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('Error cargando Google Identity Services'));
      };
      
      document.head.appendChild(script);
    });
  }

  /**
   * Carga dinámicamente la API de Google
   */
  private async loadGoogleAPI(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).gapi) {
        this.gapi = (window as any).gapi;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = () => {
        this.gapi = (window as any).gapi;
        resolve();
      };
      script.onerror = () => reject(new Error('Error al cargar Google API'));
      document.head.appendChild(script);
    });
  }

    /**
   * Verifica si el usuario está actualmente autenticado
   */
  async checkAuthStatus(): Promise<boolean> {
    try {
      console.log('Verificando estado de autenticación...');
      
      // Verificar si tenemos un token almacenado en el estado
      if (this.authState.isSignedIn && this.authState.accessToken) {
        console.log('Token encontrado en el estado, verificando validez...');
        
        // Verificar si el token sigue siendo válido
        try {
          // Inicializar gapi si no está disponible
          if (!this.gapi) {
            await this.initializeGoogleAPI();
          }

          // Configurar el token para las requests
          this.gapi.client.setToken({ access_token: this.authState.accessToken });
          
          // Hacer una petición simple para verificar si el token es válido
          const response = await fetch('https://www.googleapis.com/oauth2/v1/userinfo?alt=json', {
            headers: {
              'Authorization': `Bearer ${this.authState.accessToken}`
            }
          });
          
          if (!response.ok) {
            throw new Error('Token inválido');
          }
          
          console.log('Token válido, usuario autenticado:', this.authState.user?.email);
          return true;
        } catch (tokenError) {
          console.log('Token inválido o expirado, limpiando estado...');
          // Token inválido, limpiar estado
          this.authState = { isSignedIn: false };
          this.clearAuthStateFromStorage();
          return false;
        }
      }
      
      console.log('No hay token o estado de autenticación');
      return false;
    } catch (error) {
      console.error('Error verificando estado de autenticación:', error);
      return false;
    }
  }

  /**
   * Inicia el proceso de autenticación usando Google Identity Services
   */
  async signIn(): Promise<boolean> {
    try {
      console.log('Iniciando proceso de autenticación con Google Identity Services...');
      
      // Inicializar la API si no está inicializada
      if (!this.gapi) {
        console.log('Inicializando Google API...');
        await this.initializeGoogleAPI();
      }

      console.log('Configurando cliente OAuth2 con Google Identity Services...');
      
      // Usar Google Identity Services OAuth2
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: this.authConfig.clientId,
        scope: this.authConfig.scopes.join(' '),
        callback: (resp: any) => {
          // Callback por defecto, se sobrescribirá en la Promise
        }
      });

      console.log('Solicitando token de acceso...');
      
      // Obtener token usando una Promise
      const accessToken = await new Promise<string>((resolve, reject) => {
        tokenClient.callback = (resp: any) => {
          if (resp.error !== undefined) {
            reject(resp);
          } else {
            resolve(resp.access_token);
          }
        };
        
        // Solicitar token
        tokenClient.requestAccessToken({ prompt: 'consent' });
      });

      console.log('Token de acceso obtenido, verificando usuario...');
      
      // Usar el token para obtener información del usuario
      this.gapi.client.setToken({ access_token: accessToken });
      
      // Obtener información del perfil del usuario usando la API de UserInfo
      const userInfo = await this.getUserProfile();
      const userEmail = userInfo.email || '';
      console.log('Usuario autenticado:', userEmail);
      
      if (!this.isUserAuthorized(userEmail)) {
        console.log('Usuario no autorizado:', userEmail);
        // Revocar el token usando la API de Google
        try {
          await fetch(`https://oauth2.googleapis.com/revoke?token=${accessToken}`, {
            method: 'POST',
            headers: {
              'Content-type': 'application/x-www-form-urlencoded'
            }
          });
        } catch (revokeError) {
          console.warn('Error revocando token:', revokeError);
        }
        throw this.createAppError('USER_NOT_AUTHORIZED', 
          `El usuario ${userEmail} no está autorizado para acceder a este calendario. Contacte al administrador.`);
      }
      
      console.log('Usuario autorizado, actualizando estado...');
      this.updateAuthStateWithToken(accessToken, userInfo);
      
      return true;
    } catch (error: any) {
      console.error('Error detallado en signIn:', error);
      
      // Si el error ya es nuestro AppError, lo re-lanzamos
      if (error.code && error.message) {
        throw error;
      }
      
      // Manejo específico de errores de Google
      if (error.error) {
        let errorMessage = `Error de Google OAuth: ${error.error}`;
        
        if (error.error === 'idpiframe_initialization_failed') {
          errorMessage = 'idpiframe_initialization_failed';
        } else if (error.error === 'popup_closed_by_user') {
          errorMessage = 'popup_closed_by_user';
        } else if (error.error === 'access_denied') {
          errorMessage = 'access_denied';
        }
        
        throw this.createAppError('GOOGLE_AUTH_ERROR', errorMessage, error);
      }
      
      throw this.createAppError('SIGNIN_FAILED', 'Error al iniciar sesión', error);
    }
  }

  /**
   * Verifica si un usuario está autorizado según la whitelist
   */
  private isUserAuthorized(email: string): boolean {
    return this.ownerConfig.whitelist.includes(email.toLowerCase());
  }

  /**
   * Cierra la sesión del usuario
   */
  async signOut(): Promise<void> {
    try {
      // Revocar el token si existe
      if (this.authState.accessToken) {
        // Revocar directamente usando la API de Google
        await fetch(`https://oauth2.googleapis.com/revoke?token=${this.authState.accessToken}`, {
          method: 'POST',
          headers: {
            'Content-type': 'application/x-www-form-urlencoded'
          }
        });
      }

      this.authState = {
        isSignedIn: false
      };
      
      // Limpiar localStorage
      this.clearAuthStateFromStorage();
    } catch (error) {
      throw this.createAppError('SIGNOUT_FAILED', 'Error al cerrar sesión', error);
    }
  }

  /**
   * Obtiene el perfil del usuario usando la API de UserInfo de Google
   */
  private async getUserProfile(): Promise<any> {
    try {
      console.log('Obteniendo perfil del usuario con UserInfo API...');
      
      // Usar la API de UserInfo que es más simple y directa
      const response = await fetch('https://www.googleapis.com/oauth2/v1/userinfo?alt=json', {
        headers: {
          'Authorization': `Bearer ${this.gapi.client.getToken()?.access_token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const userInfo = await response.json();
      console.log('Información del usuario obtenida:', userInfo);
      
      return userInfo;
    } catch (error) {
      console.error('Error obteniendo perfil del usuario:', error);
      throw this.createAppError('PROFILE_ERROR', 'Error obteniendo información del usuario', error);
    }
  }

  /**
   * Actualiza el estado de autenticación con token y información del usuario
   */
  private updateAuthStateWithToken(accessToken: string, userInfo: any): void {
    console.log('Actualizando estado de autenticación con:', userInfo);

    this.authState = {
      isSignedIn: true,
      accessToken: accessToken,
      user: {
        id: userInfo.id || '',
        email: userInfo.email || '',
        name: userInfo.name || userInfo.given_name || '',
        imageUrl: userInfo.picture || '',
        accessToken: accessToken
      }
    };
    
    // Guardar en localStorage
    this.saveAuthStateToStorage();
    console.log('Estado de autenticación actualizado:', this.authState);
  }

  /**
   * Obtiene el token de acceso actual
   */
  getAccessToken(): string | null {
    return this.authState.accessToken || null;
  }

  /**
   * Obtiene la información del usuario actual
   */
  getCurrentUser(): GoogleUser | null {
    return this.authState.user || null;
  }

  /**
   * Verifica si el usuario está autenticado
   */
  isAuthenticated(): boolean {
    return this.authState.isSignedIn;
  }

  /**
   * Actualiza el estado de autenticación con la información del usuario
   */
  private updateAuthState(googleUser: any): void {
    const profile = googleUser.getBasicProfile();
    const authResponse = googleUser.getAuthResponse();

    this.authState = {
      isSignedIn: true,
      user: {
        id: profile.getId(),
        email: profile.getEmail(),
        name: profile.getName(),
        imageUrl: profile.getImageUrl()
      },
      accessToken: authResponse.access_token
    };
  }

  /**
   * Crea un objeto de error de la aplicación
   */
  private createAppError(code: string, message: string, details?: unknown): AppError {
    return {
      code,
      message,
      details
    };
  }

  /**
   * Configura el ID del cliente de Google
   */
  setClientId(clientId: string): void {
    this.authConfig.clientId = clientId;
  }

  /**
   * Obtiene la instancia de gapi.client para realizar peticiones a la API
   */
  getGapiClient(): any {
    if (!this.gapi || !this.gapi.client) {
      throw this.createAppError('GAPI_NOT_INITIALIZED', 'Google API no está inicializada');
    }
    
    if (!this.gapi.client.calendar) {
      throw this.createAppError('CALENDAR_API_NOT_LOADED', 'Google Calendar API no está cargada');
    }
    
    return this.gapi.client;
  }

  /**
   * Obtiene el email del propietario del calendario
   */
  getOwnerEmail(): string {
    return this.ownerConfig.email;
  }

  /**
   * Verifica si el usuario actual es el propietario del calendario
   */
  isCurrentUserOwner(): boolean {
    const currentUser = this.getCurrentUser();
    return currentUser ? currentUser.email.toLowerCase() === this.ownerConfig.email.toLowerCase() : false;
  }

  /**
   * Obtiene la lista de usuarios autorizados
   */
  getAuthorizedUsers(): string[] {
    return [...this.ownerConfig.whitelist];
  }

  /**
   * Agrega un usuario a la whitelist (solo el propietario puede hacer esto)
   */
  addAuthorizedUser(email: string): boolean {
    if (!this.isCurrentUserOwner()) {
      return false;
    }
    
    const normalizedEmail = email.toLowerCase();
    if (!this.ownerConfig.whitelist.includes(normalizedEmail)) {
      this.ownerConfig.whitelist.push(normalizedEmail);
      return true;
    }
    return false;
  }

  /**
   * Remueve un usuario de la whitelist (solo el propietario puede hacer esto)
   */
  removeAuthorizedUser(email: string): boolean {
    if (!this.isCurrentUserOwner() || email.toLowerCase() === this.ownerConfig.email.toLowerCase()) {
      return false; // No se puede remover al propietario
    }
    
    const normalizedEmail = email.toLowerCase();
    const index = this.ownerConfig.whitelist.indexOf(normalizedEmail);
    if (index > -1) {
      this.ownerConfig.whitelist.splice(index, 1);
      return true;
    }
    return false;
  }

  /**
   * Función de diagnóstico para verificar la configuración
   */
  diagnosticInfo(): void {
    console.log('=== DIAGNÓSTICO DE CONFIGURACIÓN ===');
    console.log('URL actual:', window.location.origin);
    console.log('Hostname:', window.location.hostname);
    console.log('Protocol:', window.location.protocol);
    console.log('Port:', window.location.port);
    console.log('Client ID:', this.authConfig.clientId);
    console.log('Scopes:', this.authConfig.scopes);
    console.log('Email propietario:', this.ownerConfig.email);
    console.log('Usuarios autorizados:', this.ownerConfig.whitelist);
    
    // Verificar configuraciones del navegador
    console.log('User Agent:', navigator.userAgent);
    console.log('Cookies habilitadas:', navigator.cookieEnabled);
    console.log('JavaScript habilitado:', true);
    
    console.log('=====================================');
    
    // Verificaciones
    const issues = [];
    
    if (!this.authConfig.clientId || this.authConfig.clientId === 'TU_GOOGLE_CLIENT_ID_AQUI') {
      issues.push('❌ Client ID no configurado');
    } else {
      issues.push('✅ Client ID configurado');
    }
    
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      issues.push('⚠️  No estás usando HTTPS (necesario para producción)');
    } else {
      issues.push('✅ Protocolo correcto');
    }
    
    if (this.ownerConfig.email.includes('TU_EMAIL_AQUI')) {
      issues.push('❌ Email del propietario no configurado');
    } else {
      issues.push('✅ Email del propietario configurado');
    }
    
    if (!navigator.cookieEnabled) {
      issues.push('❌ Cookies deshabilitadas (necesarias para Google Auth)');
    } else {
      issues.push('✅ Cookies habilitadas');
    }
    
    // Verificar si el dominio está en formato correcto para Google
    const origin = window.location.origin;
    if (origin === 'http://localhost:3000') {
      issues.push('✅ Usando localhost:3000 (correcto para desarrollo)');
    } else if (origin.startsWith('https://')) {
      issues.push('✅ Usando HTTPS (correcto para producción)');
    } else {
      issues.push('⚠️  Dominio puede no estar autorizado en Google Console');
    }
    
    // Verificar formato del Client ID
    if (this.authConfig.clientId && !this.authConfig.clientId.includes('.apps.googleusercontent.com')) {
      issues.push('❌ Client ID no tiene el formato correcto');
    } else if (this.authConfig.clientId && this.authConfig.clientId !== 'TU_GOOGLE_CLIENT_ID_AQUI') {
      issues.push('✅ Formato de Client ID correcto');
    }
    
    console.log('Estado de la configuración:');
    issues.forEach(issue => console.log(issue));
    
    if (issues.some(issue => issue.includes('❌'))) {
      console.log('🚨 Hay problemas de configuración que debes resolver');
    } else {
      console.log('✅ Configuración parece correcta');
    }
    
    // Instrucciones específicas para el error idpiframe_initialization_failed
    console.log('\n=== INSTRUCCIONES PARA ERROR IFRAME ===');
    console.log('Si ves error "idpiframe_initialization_failed":');
    console.log('1. 🍪 Habilita cookies de terceros en tu navegador');
    console.log('2. 🔓 Desactiva bloqueadores de anuncios temporalmente');
    console.log('3. 🌐 Verifica que ' + origin + ' esté en Google Console');
    console.log('4. 🔄 Prueba en modo incógnito');
    console.log('5. 🧹 Limpia cache y cookies del navegador');
    console.log('==========================================');
  }

  /**
   * Reinicia completamente el sistema de autenticación
   */
  async resetAuthentication(): Promise<void> {
    console.log('🔄 Reiniciando sistema de autenticación...');
    
    try {
      // Cerrar sesión actual si existe
      if (this.gapi && this.gapi.auth2) {
        const authInstance = this.gapi.auth2.getAuthInstance();
        if (authInstance && authInstance.isSignedIn.get()) {
          await authInstance.signOut();
        }
      }
      
      // Limpiar estado
      this.gapi = null;
      this.authState = {
        isSignedIn: false
      };
      
      // Recargar la página para limpiar completamente el estado
      console.log('🔄 Recargando página para limpiar estado...');
      window.location.reload();
      
    } catch (error) {
      console.error('Error al reiniciar autenticación:', error);
      throw this.createAppError('RESET_FAILED', 'Error al reiniciar el sistema de autenticación');
    }
  }

  /**
   * Intenta resolver automáticamente problemas comunes de OAuth
   */
  async troubleshootAndRetry(): Promise<boolean> {
    console.log('🔧 Iniciando diagnóstico y resolución automática...');
    
    // Usar el método diagnosticInfo existente
    this.diagnosticInfo();
    
    // Verificar configuración básica
    if (!this.authConfig.clientId || this.authConfig.clientId.includes('YOUR_')) {
      throw this.createAppError('CONFIG_ERROR', 'Client ID no configurado correctamente en config/index.ts');
    }
    
    // Verificar protocolo
    const protocol = window.location.protocol;
    if (protocol !== 'http:' && protocol !== 'https:') {
      throw this.createAppError('PROTOCOL_ERROR', 'El protocolo debe ser http: o https:');
    }
    
    // Para localhost, debe ser http
    if (window.location.hostname === 'localhost' && protocol !== 'http:') {
      throw this.createAppError('LOCALHOST_PROTOCOL_ERROR', 'Para localhost debe usar http:// no https://');
    }
    
    // Verificar cookies
    if (!navigator.cookieEnabled) {
      throw this.createAppError('COOKIES_DISABLED', 'Las cookies están deshabilitadas. Habilite las cookies en su navegador.');
    }
    
    console.log('✅ Configuración básica válida, reintentando inicialización...');
    
    try {
      // Reinicializar con configuración limpia
      await this.initializeGoogleAPI();
      return true;
    } catch (error) {
      console.error('Error en troubleshootAndRetry:', error);
      return false;
    }
  }
}
